import * as THREE from 'three';
import ThreeSceneObject from '../../three-base-components/ThreeSceneObject';
import { buildLODUrls, LOD_TO_GRID_SEGMENTS_MAP } from '../../Scene/sceneUtils';
import TWEEN from '@tweenjs/tween.js';
import { Geometry } from './Geometry';

//units length of the store
const STORE_SIZE = 20;
//how many points to sample on the plane.  Used to check if the face is visible or not
const PTS_COUNT = 8;
// how many requests to prioritize, currently prioritizing level 2 and 3
// so 6 * 2 = 12
const PRIORITIZED_COUNT = 12;

const generatePointsOnPlane = (constantAxis, constantAxisValue) => {
	var pts = [];
	const stepSize = STORE_SIZE / Math.sqrt(PTS_COUNT);
	for (let i = -STORE_SIZE / 2; i < STORE_SIZE / 2; i += stepSize) {
		for (let j = -STORE_SIZE / 2; j < STORE_SIZE / 2; j += stepSize) {
			if (constantAxis === 'x')
				pts.push(new THREE.Vector3(constantAxisValue, i, j));
			if (constantAxis === 'y')
				pts.push(new THREE.Vector3(i, constantAxisValue, j));
			if (constantAxis === 'z')
				pts.push(new THREE.Vector3(i, j, constantAxisValue));
		}
	}
	return pts;
};

const defaultPriorityArray = [
	// { face: 'front', level: 0 },
	// { face: 'right', level: 0 },
	// { face: 'left', level: 0 },
	// { face: 'top', level: 0 },
	// { face: 'bottom', level: 0 },
	// { face: 'back', level: 0 },
	{ face: 'front', level: 1 },
	{ face: 'right', level: 1 },
	{ face: 'left', level: 1 },
	{ face: 'top', level: 1 },
	{ face: 'bottom', level: 1 },
	{ face: 'back', level: 1 },
	{ face: 'front', level: 2 },
	{ face: 'right', level: 2 },
	{ face: 'left', level: 2 },
	{ face: 'front', level: 3 },
	{ face: 'right', level: 3 },
	{ face: 'left', level: 3 },
	{ face: 'top', level: 2 },
	{ face: 'top', level: 3 },
	{ face: 'bottom', level: 2 },
	{ face: 'bottom', level: 3 },
	{ face: 'back', level: 2 },
	{ face: 'back', level: 3 },
];

export default class ThreeBackgroundCube extends ThreeSceneObject {
	gc = [];

	constructor(camera, controller, renderer, transition) {
		super();
		//get initialized from loadCubeTextureFromPriorityArray
		this.url = null;
		this.loadOpacityMap = false;

		this.faces = this.getDefaultFaces();
		this.renderer = renderer;
		this.transition = transition;

		// default load order, gets sorted when the user drags to view different faces, gets consumed in a while loop until empty, then the event is removed.
		this.priorityArrayDefaultConfig = defaultPriorityArray;

		this.priorityArrayMap = {};

		this.camera = camera;
		this.loader = new THREE.TextureLoader();
		this.sceneObject = new THREE.Group();
		this.sceneObject.name = 'cubeBackground';

		Object.keys(this.faces).forEach((face) => {
			this.setFaceTransforms(face);
			this.sceneObject.add(this.faces[face].mesh);
		});

		this.controller = controller;
	}

	getDefaultFaces = () => {
		let x = this.createFaceGeometry(1).toBufferGeometry();
		let faces = {
			front: {
				mesh: new THREE.Mesh(
					x,
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('x', -STORE_SIZE / 2),
				LOD: 0,
			},
			right: {
				mesh: new THREE.Mesh(
					this.createFaceGeometry(1).toBufferGeometry(),
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('z', -STORE_SIZE / 2),
				LOD: 0,
			},
			left: {
				mesh: new THREE.Mesh(
					this.createFaceGeometry(1).toBufferGeometry(),
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('z', STORE_SIZE / 2),
				LOD: 0,
			},
			top: {
				mesh: new THREE.Mesh(
					this.createFaceGeometry(1).toBufferGeometry(),
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('y', STORE_SIZE / 2),
				LOD: 0,
			},
			bottom: {
				mesh: new THREE.Mesh(
					this.createFaceGeometry(1).toBufferGeometry(),
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('y', -STORE_SIZE / 2),
				LOD: 0,
			},
			back: {
				mesh: new THREE.Mesh(
					this.createFaceGeometry(1).toBufferGeometry(),
					new THREE.MeshBasicMaterial({
						color: 0x000000,
						transparent: true,
					}),
				),
				facePoints: generatePointsOnPlane('x', STORE_SIZE / 2),
				LOD: 0,
			},
		};

		return faces;
	};

	initPriorityArray = () => {
		this.priorityArrayMap[this.url] =
			this.priorityArrayDefaultConfig.slice();
		this.priorityArrayMap[this.url].forEach((i) => {
			i.initiatorUrl = this.url;
		});
	};

	getViewablePointsInFrustum = (frustum) => {
		let viewable = [];

		Object.keys(this.faces).forEach((face) => {
			const faceViewable = this.faces[face].facePoints.some((point) =>
				frustum.containsPoint(point),
			);
			faceViewable && viewable.push(face);
		});

		return viewable;
	};

	updateViewableFacesAndSortPriorityArray = () => {
		if (this.priorityArrayMap[this.url].length > PRIORITIZED_COUNT) return;
		const frustum = new THREE.Frustum();
		let matrix = new THREE.Matrix4();
		matrix.multiplyMatrices(
			this.camera.projectionMatrix,
			this.camera.matrixWorldInverse,
		);
		frustum.setFromProjectionMatrix(matrix);

		const viewable = this.getViewablePointsInFrustum(frustum);

		this.priorityArrayMap[this.url].sort((a, b) => {
			//hierarchical sorting, first sorting by if the face is visible or not, and second by the level of LOD.
			var aSortKey1 = viewable.includes(a.face) ? '0' : '1';
			var bSortKey1 = viewable.includes(b.face) ? '0' : '1';
			var aSortKey2 = a.level < b.level ? '0' : '1';
			var bSortKey2 = b.level < a.level ? '0' : '1';
			var aFinalKey = aSortKey1 + aSortKey2;
			var bFinalKey = bSortKey1 + bSortKey2;

			if (aFinalKey > bFinalKey) return 1;
			if (aFinalKey < bFinalKey) return -1;

			return 0;
		});
	};

	createFaceGeometry = (LOD) => {
		const face = new THREE.PlaneGeometry(
			STORE_SIZE,
			STORE_SIZE,
			LOD_TO_GRID_SEGMENTS_MAP[LOD],
			LOD_TO_GRID_SEGMENTS_MAP[LOD],
		);
		return new Geometry().fromBufferGeometry(face);
	};

	setFaceTransforms = (face) => {
		this.faces[face].mesh.name = face;
		this.faces[face].mesh.renderOrder = 1;

		switch (face) {
			case 'back':
				this.faces[face].mesh.position.x = STORE_SIZE / 2;
				this.faces[face].mesh.rotation.y = -Math.PI / 2;
				break;
			case 'front':
				this.faces[face].mesh.position.x = -STORE_SIZE / 2;
				this.faces[face].mesh.rotation.y = Math.PI / 2;
				break;
			case 'top':
				this.faces[face].mesh.position.y = STORE_SIZE / 2;
				this.faces[face].mesh.rotation.x = Math.PI / 2;
				break;
			case 'bottom':
				this.faces[face].mesh.position.y = -STORE_SIZE / 2;
				this.faces[face].mesh.rotation.x = -Math.PI / 2;
				break;
			case 'right':
				this.faces[face].mesh.position.z = -STORE_SIZE / 2;
				break;
			case 'left':
				this.faces[face].mesh.position.z = STORE_SIZE / 2;
				this.faces[face].mesh.rotation.y = Math.PI;
				break;
		}
	};

	createCanvasContext = (canvas) => {
		canvas.width = window.innerWidth;
		canvas.height = window.innerHeight;
		return canvas.getContext('2d');
	};

	createCopyCanvas = () => {
		this.copyCanvas = document.createElement('canvas');
		this.copyCanvasContext = this.createCanvasContext(this.copyCanvas);
		return this.copyCanvasContext;
	};

	createEffectCanvas = () => {
		this.effectCanvas = document.createElement('canvas');
		const root = document.getElementById('canvas-wrapper');
		this.effectCanvas.style.zIndex = 10;
		this.effectCanvas.style.position = 'absolute';
		this.effectCanvas.style.top = 0;
		this.effectCanvas.style.left = 0;
		this.effectCanvas.style.pointerEvents = 'none';
		this.effectCanvasContext = this.createCanvasContext(this.effectCanvas);
		root.appendChild(this.effectCanvas);
	};

	copyFirstFrameToEffectCanvas = () => {
		let domElement = this.renderer.domElement;

		this.copyCanvasContext.drawImage(domElement, 0, 0);
		this.effectCanvasContext.drawImage(this.copyCanvas, 0, 0);
	};

	updateTween = () => {
		this.animationFrame = requestAnimationFrame(this.updateTween);
		TWEEN.update();
	};

	triggerZoomFadeTransition = () => {
		this.createEffectCanvas();
		this.createCopyCanvas();
		this.copyFirstFrameToEffectCanvas();
		this.updateTween();

		const width = window.innerWidth;
		const height = window.innerHeight;

		const options = { opacity: 1, scale: 1 };

		new TWEEN.Tween(options)
			.to({ opacity: 0, scale: 2 }, 1500)
			.easing(TWEEN.Easing.Quadratic.Out)
			.onUpdate(() => {
				this.effectCanvasContext.clearRect(0, 0, width, height);
				this.effectCanvasContext.globalAlpha = options.opacity;

				const x = width / 2 - (width / 2) * options.scale;
				const y = height / 2 - (height / 2) * options.scale;

				this.effectCanvasContext.drawImage(
					this.copyCanvas,
					x,
					y,
					width * options.scale,
					height * options.scale,
				);
			})
			.start()
			.onComplete(() => {
				this.effectCanvasContext.clearRect(0, 0, width, height);
				cancelAnimationFrame(this.animationFrame);
			});
	};

	triggerAnimation = () => {
		if (this.transition && this.transition.enable) {
			if (this.transition.type === 'zoom & fade') {
				this.triggerZoomFadeTransition();
			}
		}
	};

	loadCubeTextureFromPriorityArray = async (
		url,
		opacityMapUrl,
		imageIntegrity,
		useWebp,
		skipLargest,
		onBackgroundReady,
		onBackgroundLoaded,
		materialProperties,
	) => {
		this.url = url;
		this.materialProperties = materialProperties;
		if (opacityMapUrl) {
			this.loadOpacityMap = true;
		} else {
			this.loadOpacityMap = false;
		}
		this.triggerAnimation();
		// this.dispose();
		this.initPriorityArray();
		let readyFired = false;

		window.addEventListener(
			'click',
			this.updateViewableFacesAndSortPriorityArray,
		);
		window.addEventListener(
			'touchstart',
			this.updateViewableFacesAndSortPriorityArray,
		);
		//transform into a while loop, and pop elements from the front
		let initiatorUrl = this.url;

		while (this.priorityArrayMap[this.url].length > 0) {
			if (initiatorUrl !== this.url) {
				return;
			}
			const priorityObject = this.priorityArrayMap[this.url].shift();
			let { face, level } = priorityObject;

			if (skipLargest && level === 3) {
				continue;
			}

			if (level === 2) {
				if (!readyFired) {
					onBackgroundReady(true);
					readyFired = true;
				}
			}

			initiatorUrl = priorityObject.initiatorUrl;
			const faceLODUrls = buildLODUrls(
				url,
				face,
				level,
				imageIntegrity,
				useWebp,
			);

			// await new Promise((resolve) => setTimeout(resolve, 100));
			let tiles, opTiles;
			if (this.loadOpacityMap) {
				//await both the bg tiles and the opacity tiles
				[tiles, opTiles] = await Promise.all([
					this.loadFaceTexturesAsync(faceLODUrls),
					this.loadFaceOpacitiesAsync(
						faceLODUrls.map((i) =>
							i.replace('cube_map', 'opacity_map'),
						),
					),
				]);
			} else {
				//else await only the bg images
				tiles = await this.loadFaceTexturesAsync(faceLODUrls);
			}
			if (initiatorUrl !== this.url) {
				return;
			}
			this.updateFace(face, level);
			this.faces[face].mesh.material = tiles;
			// await new Promise((resolve) => setTimeout(resolve, 100)); // 250ms delay
			if (this.loadOpacityMap) {
				for (
					var i = 0;
					i < this.faces[face].mesh.material.length;
					i++
				) {
					let u = this.faces[face].mesh.material[i];
					u.alphaMap = opTiles[i];
				}
			}
			this.faces[face].LOD += 1;
			window.dispatchEvent(new Event('backgroundLevelLoaded'));
		}

		this.updateViewableFacesAndSortPriorityArray();
		window.removeEventListener(
			'click',
			this.updateViewableFacesAndSortPriorityArray,
		);
		window.removeEventListener(
			'touchstart',
			this.updateViewableFacesAndSortPriorityArray,
		);
		onBackgroundLoaded();

		this.purgeGC();
	};

	purgeGC = () => {
		while (this.gc) {
			const _next = this.gc.pop();
			if (_next === undefined) {
				return;
			}
			_next?.map?.dispose();
			_next.map = null;
			_next?.dispose();
			_next.needsUpdate = true;
		}
	};

	loadTileMaterialAsync = (tileUrl) => {
		//return a single tile
		const transparent = !!this?.materialProperties?.transparent;
		const depthTest = this?.materialProperties?.depth_test === true;
		return this.loader.loadAsync(tileUrl).then((texture) => {
			texture.colorSpace = 'srgb';
			texture.minFilter = THREE.LinearMipmapNearestFilter;
			texture.magFilter = THREE.LinearFilter;
			return new THREE.MeshBasicMaterial({
				map: texture,
				transparent: transparent,
				depthTest: depthTest,
				depthWrite: true,
				toneMapped: false,
			});
		});
	};

	loadOpacityTextureAsync = (tileUrl) => {
		return new Promise((resolve, reject) => {
			this.loader.load(tileUrl, (texture) => {
				texture.minFilter = THREE.LinearMipmapNearestFilter;
				texture.magFilter = THREE.LinearFilter;
				resolve(texture);
			});
		});
	};

	loadFaceTexturesAsync = (faceLODUrls) => {
		// return a group of tiles
		return Promise.all(faceLODUrls.map(this.loadTileMaterialAsync));
	};

	loadFaceOpacitiesAsync = (faceLODUrls) => {
		// return a group of tiles
		return Promise.all(faceLODUrls.map(this.loadOpacityTextureAsync));
	};

	updateFace = (face, newLevel) => {
		if (this.faces[face].mesh.material.length) {
			this.faces[face].mesh.material.forEach((material) => {
				this.gc.push(material);
			});
		}
		this.faces[face].mesh.geometry = this.createFaceGeometry(newLevel);
		this.setupFaceUV(this.faces[face].mesh.geometry);
		this.resolveFaceMaterialIndexes(this.faces[face].mesh.geometry, face);
	};

	setDepthTest = (depthTest) => {
		this.materialProperties.depth_test = depthTest;
		Object.keys(this.faces).forEach((key) => {
			this.faces[key].mesh.children.forEach((child) => {
				child.material.depthTest = depthTest || false;
				child.material.needsUpdate = true;
			});
		});
	};

	// Build load order of cubemaps
	resolveFaceMaterialIndexes = (faceGeometry, face) => {
		// eslint-disable-line
		let currentMaterialIndex = 0;
		faceGeometry.faces.forEach((face, index) => {
			face.materialIndex = currentMaterialIndex; // eslint-disable-line
			if (index % 2 === 1) {
				currentMaterialIndex += 1;
			}
		});
		this.faces[face].mesh.geometry = faceGeometry.toBufferGeometry();
	};

	setupFaceUV = (geometry) => {
		const uvArr = geometry.faceVertexUvs[0];
		uvArr.forEach((faceUV, index) => {
			if (index % 2 === 0) {
				faceUV[0].x = 0;
				faceUV[0].y = 1;
				faceUV[1].x = 0;
				faceUV[1].y = 0;
				faceUV[2].x = 1;
				faceUV[2].y = 1;
			} else {
				faceUV[0].x = 0;
				faceUV[0].y = 0;
				faceUV[1].x = 1;
				faceUV[1].y = 0;
				faceUV[2].x = 1;
				faceUV[2].y = 1;
			}
		});
	};

	removeAlphaMaps = () => {
		if (!Array.isArray(this.faces.back.mesh.material)) return;
		this.faces.front.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
		this.faces.back.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
		this.faces.top.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
		this.faces.bottom.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
		this.faces.right.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
		this.faces.left.mesh.material.forEach((mat) => {
			mat.needsUpdate = true;
			mat.alphaMap = null;
		});
	};

	disposeMaterialMap = (material) => {
		if (material.map) {
			material.map?.dispose();
			material.map = null;
		}

		material.dispose();
		material.needsUpdate = true;
	};

	dispose = () => {
		Object.keys(this.faces).forEach((face) => {
			let currentFace = this.faces[face];
			currentFace?.mesh?.geometry?.dispose();
			if (currentFace.mesh.material) {
				if (Array.isArray(currentFace.mesh.material)) {
					for (let i = 0; i < currentFace.mesh.material.length; ++i) {
						this.disposeMaterialMap(currentFace.mesh.material[i]);
					}
				} else {
					this.disposeMaterialMap(currentFace.mesh.material);
				}
			}
		});
	};
}
